import React from 'react';
import { 
  Heart, 
  Scissors, 
  Wrench, 
  Briefcase, 
  Code2,
  GraduationCap,
  ArrowRight
} from 'lucide-react';

const roles = [
  {
    icon: Heart,
    title: "Healthcare Professionals",
    subtitle: "Nurses, Therapists, Medical Staff",
    description: "Specialized career paths, certification tracking, and salary benchmarks for healthcare roles.",
    features: ["Certification management", "Patient care skills", "Healthcare salary data"],
    gradient: "from-red-500 to-pink-500"
  },
  {
    icon: Scissors,
    title: "Beauty & Wellness",
    subtitle: "Hairdressers, Estheticians, Spa Professionals",
    description: "Build your clientele, master new techniques, and grow your beauty business.",
    features: ["Client management", "Technique mastery", "Business growth"],
    gradient: "from-purple-500 to-pink-500"
  },
  {
    icon: Wrench,
    title: "Skilled Trades",
    subtitle: "Electricians, Plumbers, Mechanics",
    description: "Advance your trade skills, earn certifications, and maximize your earning potential.",
    features: ["Trade certifications", "Safety protocols", "Union opportunities"],
    gradient: "from-orange-500 to-yellow-500"
  },
  {
    icon: Briefcase,
    title: "Business Professionals",
    subtitle: "Managers, Consultants, Sales",
    description: "Leadership development, strategic thinking, and business acumen for corporate success.",
    features: ["Leadership skills", "Strategic thinking", "Team management"],
    gradient: "from-blue-500 to-cyan-500"
  },
  {
    icon: Code2,
    title: "Technology",
    subtitle: "Developers, Designers, IT Specialists",
    description: "Stay current with tech trends, build your portfolio, and advance in the digital world.",
    features: ["Technical skills", "Portfolio building", "Industry trends"],
    gradient: "from-green-500 to-emerald-500"
  },
  {
    icon: GraduationCap,
    title: "Education & Training",
    subtitle: "Teachers, Trainers, Coaches",
    description: "Enhance your teaching methods, pursue advanced degrees, and impact more lives.",
    features: ["Teaching methods", "Curriculum design", "Student engagement"],
    gradient: "from-indigo-500 to-purple-500"
  }
];

export default function Roles() {
  return (
    <section id="roles" className="py-20 bg-secondary/5">
      <div className="mx-auto max-w-6xl px-4 md:px-6">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Tailored for your profession
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Whether you're in healthcare, trades, business, or tech—we understand your unique career path and challenges.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {roles?.map((role, index) => {
            const IconComponent = role?.icon;
            return (
              <div key={index} className="group relative overflow-hidden rounded-2xl bg-card border border-border hover:border-primary/20 transition-all duration-300 hover:shadow-lg">
                {/* Gradient background */}
                <div className={`absolute inset-0 bg-gradient-to-br ${role?.gradient} opacity-5 group-hover:opacity-10 transition-opacity`}></div>
                
                <div className="relative p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`p-3 rounded-xl bg-gradient-to-br ${role?.gradient} shadow-lg`}>
                      {IconComponent && <IconComponent className="w-6 h-6 text-white" />}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-foreground">
                        {role?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {role?.subtitle}
                      </p>
                    </div>
                  </div>
                  
                  <p className="text-muted-foreground mb-4 leading-relaxed">
                    {role?.description}
                  </p>
                  
                  <div className="space-y-2 mb-4">
                    {role?.features?.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center gap-2 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        <span className="text-muted-foreground">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <button className="group/btn inline-flex items-center gap-2 text-primary text-sm font-medium hover:gap-3 transition-all duration-200">
                    Learn more
                    <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-16 text-center">
          <p className="text-muted-foreground mb-4">
            Don't see your profession? We're constantly adding new career paths.
          </p>
          <button className="inline-flex items-center gap-2 text-primary font-medium hover:underline">
            Request your profession
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}